import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import {
  TaxDocumentType,
  TaxSupportingDocsSubType,
} from '@/pages/tax/routes/TaxDocumentsRecord/constants/tax-documents.enum';
import type { TaxYearOcrSuggestions } from '@/pages/tax/routes/TaxWorkflow/types/ocr-suggestions.types';
import {
  broadcastTaxDocEdit,
  type TaxDocEditPatch,
} from '@/pages/tax/routes/TaxDocumentsRecord/sync/taxDocumentSync';

export interface TaxDocumentDrive {
  fileId?: string;
  name?: string;
  mimeType?: string;
  webViewLink?: string;
}

export interface TaxDocumentBlob {
  container: string;
  blobName: string;
  url: string;
  contentType?: string | null;
}

export interface TaxDocumentCopiedMeta {
  newName: string;
  destFolderId: string;
  finalId: string;
  at: string;
  from: string;
}

export interface TaxDocumentIdentifiers {
  term: string | null;
  bank_name: string | null;
  account_last4: string | null;
}

export interface TaxDocumentExtras {
  amount: number | null;
  item: string | null;
  company: string | null;
  property: string | null;
  term: string | null;
}

export interface TaxDocumentDataSubitem {
  label: string;
  amount: number | null;
  comment: string | null;
}

export interface TaxDocumentAdditionalSubType {
  sub_type: TaxSupportingDocsSubType;
  amount: number;
}

export interface TaxDocumentData {
  amount: number | null;
  subitems: TaxDocumentDataSubitem[];
  additional_sub_types?: TaxDocumentAdditionalSubType[];
}

/**
 * The full classified payload as stored in diExtractRaw.classified.
 * Still returned so the frontend has access to the raw AI output if needed.
 */
export interface TaxDocumentClassified {
  category: string;
  sub_type: string;
  start_date: string | null;
  end_date: string | null;
  financial_year_end: number | null;
  identifiers: TaxDocumentIdentifiers | null;
  extras: TaxDocumentExtras | null;
  data: TaxDocumentData | null;
}

/* ── record shape returned by getFileStructure ── */

export interface TaxDocumentRecord {
  id: string;
  providerFileId: string;
  docType: TaxDocumentType;
  docSubType?: TaxSupportingDocsSubType;
  status?: string;
  email: string | null;

  drive: TaxDocumentDrive;
  blob: TaxDocumentBlob | null;
  copied: TaxDocumentCopiedMeta | null;
  property: { key: string; displayName: string } | null;

  newName?: string | null;
  /** Raw classified payload (nested, for reference) */
  classified: TaxDocumentClassified | null;

  /** Promoted top-level fields from classified */
  data: TaxDocumentData | null;
  extras: TaxDocumentExtras | null;
  financialYearEnd: number | null;
  startDate: string | null;
  endDate: string | null;
  issueDate: string | null;
  identifiers: TaxDocumentIdentifiers | null;
  createdAt: string;
  updatedAt: string;
}

export interface SingleFileTaxOrchestrationRequest {
  taxDealId: string;
  gdriveFileId: string;
  emailAddress: string;
  financialYearEnd?: number | null;
}

export interface FileUploadEntry {
  displayName?: string;
  docType?: TaxDocumentType;
  subType?: TaxSupportingDocsSubType;
  skipCategorization?: boolean;
}

export interface UploadPdfsToDriveRequest {
  taxDealId: string;
  emailAddress: string;
  skipCategorization: boolean;
  financialYearEnd?: number | null;
  fileUploads: FileUploadEntry[];
  files: File[];
}

export interface UpdateTaxDocumentRequest {
  documentId: string;
  newName?: string;
  docType?: TaxDocumentType;
  subType?: TaxSupportingDocsSubType;
  financialYearEnd?: number;
  /** Registry displayName or new address; null clears the assignment. */
  property?: string | null;
}

export interface TaxPropertyYearFolder {
  folderId: string;
  subFolders: Record<string, string>;
}

export interface TaxPropertyFolderEntry {
  key: string;
  displayName: string;
  aliases: string[];
  propertyId: string | null;
  /** Drive folders keyed by financial year, e.g. { "2026": {...} }. */
  years?: Record<string, TaxPropertyYearFolder>;
  /** True when the folder was auto-created from a document, not a linked property. */
  autoCreated?: boolean;
}

export interface TaxPropertyFolders {
  /**
   * The Drive registry, whole — for the manage/rename/merge screens only.
   * Every tax-scoped linked property is in here, even one with no Drive folder
   * yet (empty `years`), so it can be renamed or used as a merge target.
   */
  properties: TaxPropertyFolderEntry[];
  /**
   * The only properties a document may be filed under: linked properties in
   * the deal's tax scope, named as the registry names them. Use this for every
   * property picker — auto-created folders are intentionally excluded.
   */
  assignable: { propertyId: string; displayName: string }[];
}

export interface RenamePropertyFolderArgs {
  taxDealId: string;
  key: string;
  displayName: string;
}

export interface MergePropertyFoldersArgs {
  taxDealId: string;
  /** Registry keys being merged (must include targetKey). */
  sourceKeys: string[];
  /** The property everything merges INTO — its folder/name is kept. */
  targetKey: string;
}

export interface UpdateTaxDocumentArgs extends UpdateTaxDocumentRequest {
  taxDealId?: string;
}

export type SuggestedPropertyDoc = {
  documentId: string;
  documentName: string;
  financialYearEnd: number;
};

export interface SuggestedProperty {
  address: string;
  documents: SuggestedPropertyDoc[];
  source: 'RENTAL_STATEMENTS' | 'COUNCIL_RATES' | 'PRE_LODGEMENT';
  checklistUrl?: string;
}

/* ── shared edit-apply logic ──
 * One source of truth for how an edit mutates a cached document, used by:
 *   1. the local optimistic update in updateTaxDocumentInfo.onQueryStarted
 *   2. remote edits arriving from another tab/window (applyRemoteTaxDocEdit) */
const applyEditToDraft = (
  draft: TaxDocumentRecord[],
  documentId: string,
  patch: TaxDocEditPatch,
): void => {
  const doc = draft.find((d) => d.id === documentId);
  if (!doc) return;
  if (patch.newName !== undefined) {
    doc.newName = patch.newName;
    if (doc.copied) doc.copied.newName = patch.newName;
  }
  if (patch.docType !== undefined)
    doc.docType = patch.docType as TaxDocumentType;
  doc.docSubType = patch.subType as TaxSupportingDocsSubType | undefined;
  doc.financialYearEnd = patch.financialYearEnd ?? null;
  if (patch.property !== undefined) {
    doc.property = patch.property
      ? { key: patch.property, displayName: patch.property }
      : null;
    if (doc.extras) doc.extras.property = patch.property ?? null;
    if (doc.classified?.extras)
      doc.classified.extras.property = patch.property ?? null;
  }
};

/* ── API slice ── */

export const taxDocumentsApi = createApi({
  reducerPath: 'taxDocumentsApi',
  baseQuery: customBaseQuery,
  tagTypes: [
    'TaxDocuments',
    'OcrSuggestions',
    'SuggestedProperties',
    'PropertyFolders',
  ],
  endpoints: (builder) => ({
    listTaxDocuments: builder.query<TaxDocumentRecord[], string>({
      query: (taxDealId) => ({
        url: `v1/tax/tax-documents/${taxDealId}`,
        method: 'GET',
      }),
      transformResponse: (response: {
        statusCode: number;
        message: string;
        data: TaxDocumentRecord[];
      }) =>
        response.data.map((d) => ({
          ...d,
          financialYearEnd:
            d.financialYearEnd ?? d.classified?.financial_year_end ?? null,
          startDate: d.startDate ?? d.classified?.start_date ?? null,
          endDate: d.endDate ?? d.classified?.end_date ?? null,
        })),
      providesTags: (_result, _error, taxDealId) => [
        { type: 'TaxDocuments', id: taxDealId },
      ],
    }),

    startTaxOrchestration: builder.mutation<TaxDocumentRecord[], string>({
      query: (taxDealId) => ({
        url: `v1/tax/tax-documents/tax-orchestration/${taxDealId}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, taxDealId) => [
        { type: 'TaxDocuments', id: taxDealId },
        { type: 'SuggestedProperties', id: taxDealId },
        { type: 'PropertyFolders', id: taxDealId },
      ],
    }),

    startSingleFileTaxOrchestration: builder.mutation<
      TaxDocumentRecord[],
      SingleFileTaxOrchestrationRequest
    >({
      query: ({ taxDealId, ...body }) => ({
        url: `v1/tax/tax-documents/tax-orchestration/${taxDealId}/file`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { taxDealId }) => [
        { type: 'TaxDocuments', id: taxDealId },
        { type: 'SuggestedProperties', id: taxDealId },
        { type: 'PropertyFolders', id: taxDealId },
      ],
    }),

    updateTaxDocumentInfo: builder.mutation<
      TaxDocumentRecord,
      UpdateTaxDocumentArgs
    >({
      query: ({ taxDealId: _, ...body }) => ({
        url: 'v1/tax/tax-documents/update-document',
        method: 'POST',
        body,
      }),
      async onQueryStarted(
        {
          taxDealId,
          documentId,
          newName,
          docType,
          subType,
          financialYearEnd,
          property,
        },
        { dispatch, queryFulfilled },
      ) {
        if (!taxDealId) return;
        const editPatch: TaxDocEditPatch = {
          ...(newName !== undefined ? { newName } : {}),
          ...(docType !== undefined ? { docType } : {}),
          ...(property !== undefined ? { property } : {}),
          subType,
          financialYearEnd: financialYearEnd ?? null,
        };
        const patch = dispatch(
          taxDocumentsApi.util.updateQueryData(
            'listTaxDocuments',
            taxDealId,
            (draft) => applyEditToDraft(draft, documentId, editPatch),
          ),
        );
        try {
          await queryFulfilled;
          // Only broadcast once the server has confirmed the write, so other
          // tabs never apply an edit that ends up rolled back here.
          broadcastTaxDocEdit(taxDealId, documentId, editPatch);
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, { taxDealId }) =>
        taxDealId
          ? [
              { type: 'TaxDocuments', id: taxDealId },
              { type: 'PropertyFolders', id: taxDealId },
            ]
          : [{ type: 'TaxDocuments' as const }],
    }),

    getOcrSuggestions: builder.query<TaxYearOcrSuggestions[], string>({
      query: (taxDealId) => ({
        url: `v1/tax/tax-documents/ocr-data/${taxDealId}`,
        method: 'GET',
      }),
      transformResponse: (response: {
        statusCode: number;
        message: string;
        data: TaxYearOcrSuggestions[];
      }) => response.data,
      providesTags: (_result, _error, taxDealId) => [
        { type: 'OcrSuggestions', id: taxDealId },
      ],
    }),
    uploadPdfsToDrive: builder.mutation<void, UploadPdfsToDriveRequest>({
      query: ({
        taxDealId,
        emailAddress,
        skipCategorization,
        financialYearEnd,
        fileUploads,
        files,
      }) => {
        const formData = new FormData();
        formData.append('taxDealId', taxDealId);
        formData.append('emailAddress', emailAddress);
        formData.append('skipCategorization', String(skipCategorization));
        if (financialYearEnd != null) {
          formData.append('financialYearEnd', String(financialYearEnd));
        }
        formData.append('fileUploads', JSON.stringify(fileUploads));
        files.forEach((file) => formData.append('files', file, file.name));

        return {
          url: 'v1/tax/tax-documents/upload-pdfs-to-drive',
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: (_result, _error, { taxDealId }) => [
        { type: 'TaxDocuments', id: taxDealId },
        { type: 'SuggestedProperties', id: taxDealId },
        { type: 'PropertyFolders', id: taxDealId },
      ],
    }),

    getPropertyFolders: builder.query<TaxPropertyFolders, string>({
      query: (taxDealId) => ({
        url: `v1/tax/tax-documents/property-folders/${taxDealId}`,
        method: 'GET',
      }),
      transformResponse: (response: {
        statusCode: number;
        message: string;
        data: TaxPropertyFolders;
      }) => response.data,
      providesTags: (_r, _e, taxDealId) => [
        { type: 'PropertyFolders', id: taxDealId },
      ],
    }),

    renamePropertyFolder: builder.mutation<
      TaxPropertyFolders,
      RenamePropertyFolderArgs
    >({
      query: ({ taxDealId, key, displayName }) => ({
        url: `v1/tax/tax-documents/property-folders/${taxDealId}/rename`,
        method: 'PATCH',
        body: { key, displayName },
      }),
      transformResponse: (response: {
        statusCode: number;
        message: string;
        data: TaxPropertyFolders;
      }) => response.data,
      invalidatesTags: (_r, _e, { taxDealId }) => [
        { type: 'PropertyFolders', id: taxDealId },
        { type: 'TaxDocuments', id: taxDealId },
      ],
    }),

    mergePropertyFolders: builder.mutation<
      TaxPropertyFolders,
      MergePropertyFoldersArgs
    >({
      query: ({ taxDealId, sourceKeys, targetKey }) => ({
        url: `v1/tax/tax-documents/property-folders/${taxDealId}/merge`,
        method: 'POST',
        body: { sourceKeys, targetKey },
      }),
      transformResponse: (response: {
        statusCode: number;
        message: string;
        data: TaxPropertyFolders;
      }) => response.data,
      invalidatesTags: (_r, _e, { taxDealId }) => [
        { type: 'PropertyFolders', id: taxDealId },
        { type: 'TaxDocuments', id: taxDealId },
      ],
    }),

    getSuggestedProperties: builder.query<SuggestedProperty[], string>({
      query: (taxDealId) => ({
        url: `v1/tax/tax-documents/suggested-properties/${taxDealId}`,
        method: 'GET',
      }),
      transformResponse: (response: {
        statusCode: number;
        message: string;
        data: SuggestedProperty[];
      }) => response.data,
      providesTags: (_r, _e, taxDealId) => [
        { type: 'SuggestedProperties', id: taxDealId },
      ],
    }),
  }),
});

/**
 * Returns the two RTK actions needed to apply a remote tab edit into local cache.
 * Dispatch both at the call site:
 *
 *   subscribeTaxDocEdits((msg) => {
 *     if (msg.taxDealId !== dealId) return;
 *     const [update, invalidate] = remoteEditActions(msg.taxDealId, msg.documentId, msg.patch);
 *     dispatch(update);
 *     dispatch(invalidate);
 *   });
 *
 * Exporting actions (not a thunk) avoids the circular RootState import that
 * ThunkDispatch<unknown, ...> would require.
 */
export const remoteEditActions = (
  taxDealId: string,
  documentId: string,
  patch: TaxDocEditPatch,
) =>
  [
    taxDocumentsApi.util.updateQueryData(
      'listTaxDocuments',
      taxDealId,
      (draft) => applyEditToDraft(draft, documentId, patch),
    ),
    taxDocumentsApi.util.invalidateTags([
      { type: 'TaxDocuments', id: taxDealId },
    ]),
  ] as const;

export const {
  useListTaxDocumentsQuery,
  useStartTaxOrchestrationMutation,
  useStartSingleFileTaxOrchestrationMutation,
  useUpdateTaxDocumentInfoMutation,
  useGetOcrSuggestionsQuery,
  useUploadPdfsToDriveMutation,
  useGetPropertyFoldersQuery,
  useRenamePropertyFolderMutation,
  useMergePropertyFoldersMutation,
  useGetSuggestedPropertiesQuery,
} = taxDocumentsApi;
